import { AnimatePresence, motion } from 'framer-motion'
import { Check, Eye, EyeOff, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useModal } from './ModalContext'

// ─── Masks ────────────────────────────────────────────────────────────────────

function maskPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11)
  if (!d) return ''
  if (d.length <= 2) return `(${d}`
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

function maskCpfCnpj(v) {
  const d = v.replace(/\D/g, '')
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  }
  return d
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
}

function maskDate(v) {
  return v
    .replace(/\D/g, '')
    .slice(0, 8)
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
}

// ─── Validators ───────────────────────────────────────────────────────────────

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
}

function isValidCpf(d) {
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false
  let s = 0
  for (let i = 0; i < 9; i++) s += +d[i] * (10 - i)
  let r = (s * 10) % 11; if (r >= 10) r = 0
  if (r !== +d[9]) return false
  s = 0
  for (let i = 0; i < 10; i++) s += +d[i] * (11 - i)
  r = (s * 10) % 11; if (r >= 10) r = 0
  return r === +d[10]
}

function isValidCnpj(d) {
  if (d.length !== 14 || /^(\d)\1+$/.test(d)) return false
  const calc = (len) => {
    let s = 0, p = len - 7
    for (let i = len; i >= 1; i--) { s += +d[len - i] * p--; if (p < 2) p = 9 }
    const r = s % 11; return r < 2 ? 0 : 11 - r
  }
  return calc(12) === +d[12] && calc(13) === +d[13]
}

function isValidCpfOrCnpj(v) {
  const d = v.replace(/\D/g, '')
  if (d.length === 11) return isValidCpf(d)
  if (d.length === 14) return isValidCnpj(d)
  return false
}

function isValidDate(v) {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return false
  const [dd, mm, yyyy] = v.split('/').map(Number)
  const date = new Date(yyyy, mm - 1, dd)
  return date.getFullYear() === yyyy && date.getMonth() === mm - 1 && date.getDate() === dd
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const LABEL = 'block text-[13px] font-semibold text-text-secondary mb-1.5'
const ERROR = 'text-[11px] text-status-error mt-1'

function inputCls(hasError) {
  return [
    'w-full rounded-input border px-4 py-3 text-sm text-text-primary',
    'bg-surface-page placeholder:text-text-muted',
    'focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-border-input-focus',
    'transition-colors',
    hasError ? 'border-status-error' : 'border-border-input',
  ].join(' ')
}

// ─── Step tabs ────────────────────────────────────────────────────────────────

const TABS = ['1. Seus dados', '2. Cadastro']

function StepTabs({ current }) {
  return (
    <div className="flex gap-6 border-b border-border-input mb-5">
      {TABS.map((label, i) => {
        const active = current === i + 1
        return (
          <span
            key={label}
            className={`pb-3 text-sm font-semibold transition-colors select-none ${
              active
                ? 'text-accent-primary border-b-2 border-accent-primary -mb-px'
                : 'text-text-muted'
            }`}
          >
            {label}
          </span>
        )
      })}
    </div>
  )
}

// ─── Field ────────────────────────────────────────────────────────────────────

function Field({ label, error, children }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      {children}
      {error && <p className={ERROR}>{error}</p>}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const EMPTY = {
  nome: '', sobrenome: '', email: '', telefone: '',
  cpfCnpj: '', dataNascimento: '', senha: '', confirmarSenha: '',
}

export default function WaitlistModal() {
  const { isOpen, plan, planId, closeModal } = useModal()
  const [step, setStep] = useState(1)
  const [success, setSuccess] = useState(false)
  const [data, setData] = useState(EMPTY)
  const [erros, setErros] = useState({})
  const [showSenha, setShowSenha] = useState(false)
  const [showConfirmar, setShowConfirmar] = useState(false)
  const API = 'http://localhost:3000'
  const [loading, setLoading] = useState(false)
  const [erroGeral, setErroGeral] = useState(null)

  useEffect(() => {
    if (isOpen) {
      setStep(1)
      setSuccess(false)
      setData(EMPTY)
      setErros({})
      setShowSenha(false)
      setShowConfirmar(false)
      setErroGeral(null)
    }
  }, [isOpen])

  function set(field, value) {
    setData(d => ({ ...d, [field]: value }))
    if (erros[field]) setErros(e => ({ ...e, [field]: null }))
  }

  function validarEtapa1() {
    const e = {}
    if (!data.nome.trim()) e.nome = 'Nome obrigatório'
    if (!data.sobrenome.trim()) e.sobrenome = 'Sobrenome obrigatório'
    if (!data.email.trim()) e.email = 'E-mail obrigatório'
    else if (!isValidEmail(data.email)) e.email = 'E-mail inválido'
    if (!data.telefone.trim()) e.telefone = 'Telefone obrigatório'
    else if (data.telefone.replace(/\D/g, '').length < 10) e.telefone = 'Telefone inválido'
    setErros(e)
    return Object.keys(e).length === 0
  }

  function validarEtapa2() {
    const e = {}
    if (!data.cpfCnpj.trim()) e.cpfCnpj = 'CPF/CNPJ obrigatório'
    else if (!isValidCpfOrCnpj(data.cpfCnpj)) e.cpfCnpj = 'CPF/CNPJ inválido'
    if (!data.dataNascimento.trim()) e.dataNascimento = 'Data de nascimento obrigatória'
    else if (!isValidDate(data.dataNascimento)) e.dataNascimento = 'Data inválida'
    if (!data.senha) e.senha = 'Senha obrigatória'
    else if (data.senha.length < 8) e.senha = 'Mínimo 8 caracteres'
    if (!data.confirmarSenha) e.confirmarSenha = 'Confirmação obrigatória'
    else if (data.senha !== data.confirmarSenha) e.confirmarSenha = 'As senhas não coincidem'
    setErros(e)
    return Object.keys(e).length === 0
  }

  async function handleContinuar() {
    if (!validarEtapa1()) return

    try {
      await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: data.nome,
          sobrenome: data.sobrenome,
          email: data.email,
          telefone: data.telefone,
          plan_id: planId,
          plano: plan,
        }),
      })
    } catch (_) {
      // lead é best-effort, não bloqueia o fluxo
    }

    setStep(2)
  }

  async function handleSubmit() {
    if (!validarEtapa2()) return

    setLoading(true)
    try {
      const res = await fetch(`${API}/api/v1/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: { 
            id: planId 
          },
          customer: {
            first_name: data.nome,
            last_name: data.sobrenome,
            email: data.email,
            phone: data.telefone,
            document: data.cpfCnpj.replace(/\D/g, ''),
            date_of_birth: data.dataNascimento.split('/').reverse().join('-'),
            password: data.senha,
            password_confirmation: data.confirmarSenha,
          }
        }),
      })

      if (!res.ok) {
        const body = await res.json()
        if (body?.errors?.email) {
          setErros(e => ({ ...e, email: 'E-mail já cadastrado' }))
          setStep(1)
        } else if (body?.errors?.cpf_cnpj) {
          setErros(e => ({ ...e, cpfCnpj: 'CPF/CNPJ já cadastrado' }))
        } else {
          setErroGeral('Erro ao realizar cadastro. Tente novamente.')
        }
        return
      }

      const loginRes = await fetch(`${API}/auth/sign_in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer: {
            email: data.email,
            password: data.senha,
          }
        }),
      })

      if (loginRes.ok) {
        window.location.href = '/portal'
      } else {
        setSuccess(true)
        setTimeout(closeModal, 3000)
      }
    } catch (_) {
      setErroGeral('Erro de conexão. Verifique sua internet.')
    } finally {
      setLoading(false)
    }
  }

  const TITLES = {
    1: 'Seus dados',
    2: 'Complete seu cadastro',
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={closeModal}
        >
          <div className="absolute inset-0 bg-black/60" />

          <motion.div
            className="relative z-10 bg-surface-card rounded-card shadow-xl w-full max-w-lg p-8"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-text-muted hover:text-text-primary transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>

            {success ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 rounded-full bg-status-success/10 flex items-center justify-center mb-6">
                  <Check className="w-8 h-8 text-status-success" strokeWidth={2.5} />
                </div>
                <h2 className="text-[20px] font-bold text-text-primary mb-2">
                  Tudo certo!
                </h2>
                <p className="text-[14px] text-text-secondary max-w-xs">
                  Entraremos em contato em breve com suas condições de fundador.
                </p>
              </div>
            ) : (
              <>
                {erroGeral && (
                  <p className="text-[12px] text-status-error mb-4">{erroGeral}</p>
                )}

                <StepTabs current={step} />

                {plan && (
                  <p className="text-[13px] text-text-muted mb-1">
                    Você está reservando o{' '}
                    <span className="text-accent-primary font-semibold">Plano {plan}</span>
                  </p>
                )}

                <h2 className="text-[20px] font-bold text-text-primary mb-6">
                  {TITLES[step]}
                </h2>

            {/* ── Etapa 1 ── */}
            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Nome" error={erros.nome}>
                    <input
                      className={inputCls(erros.nome)}
                      placeholder="João"
                      value={data.nome}
                      onChange={e => set('nome', e.target.value)}
                    />
                  </Field>
                  <Field label="Sobrenome" error={erros.sobrenome}>
                    <input
                      className={inputCls(erros.sobrenome)}
                      placeholder="Silva"
                      value={data.sobrenome}
                      onChange={e => set('sobrenome', e.target.value)}
                    />
                  </Field>
                </div>

                <Field label="E-mail" error={erros.email}>
                  <input
                    type="email"
                    className={inputCls(erros.email)}
                    placeholder="joao@email.com"
                    value={data.email}
                    onChange={e => set('email', e.target.value)}
                  />
                </Field>

                <Field label="Telefone" error={erros.telefone}>
                  <input
                    type="tel"
                    className={inputCls(erros.telefone)}
                    placeholder="(81) 99999-9999"
                    value={data.telefone}
                    onChange={e => set('telefone', maskPhone(e.target.value))}
                  />
                </Field>

                <button onClick={handleContinuar} className="btn-primary w-full mt-2">
                  Continuar
                </button>
              </div>
            )}

            {/* ── Etapa 2 ── */}
            {step === 2 && (
              <div className="flex flex-col gap-4">
                <Field label="CPF / CNPJ" error={erros.cpfCnpj}>
                  <input
                    className={inputCls(erros.cpfCnpj)}
                    placeholder="000.000.000-00"
                    value={data.cpfCnpj}
                    onChange={e => set('cpfCnpj', maskCpfCnpj(e.target.value))}
                  />
                </Field>

                <Field label="Data de nascimento" error={erros.dataNascimento}>
                  <input
                    className={inputCls(erros.dataNascimento)}
                    placeholder="DD/MM/AAAA"
                    value={data.dataNascimento}
                    onChange={e => set('dataNascimento', maskDate(e.target.value))}
                  />
                </Field>

                <Field label="Senha" error={erros.senha}>
                  <div className="relative">
                    <input
                      type={showSenha ? 'text' : 'password'}
                      className={`${inputCls(erros.senha)} pr-11`}
                      placeholder="Mínimo 8 caracteres"
                      value={data.senha}
                      onChange={e => set('senha', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSenha(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                    >
                      {showSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>

                <Field label="Confirmar senha" error={erros.confirmarSenha}>
                  <div className="relative">
                    <input
                      type={showConfirmar ? 'text' : 'password'}
                      className={`${inputCls(erros.confirmarSenha)} pr-11`}
                      placeholder="Repita a senha"
                      value={data.confirmarSenha}
                      onChange={e => set('confirmarSenha', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmar(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                    >
                      {showConfirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </Field>

                <div className="flex gap-3 mt-2">
                  <button onClick={() => { setStep(1); setErros({}) }} className="btn-ghost flex-shrink-0">
                    Voltar
                  </button>
                  <button onClick={handleSubmit} disabled={loading} className="btn-primary flex-1">
                    {loading ? 'Enviando...' : 'Garantir minha vaga'}
                  </button>
                </div>
              </div>
            )}
              </>
            )}

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
